import { ExternalLink } from 'lucide-react';

import { ContactValue, DetailSection, Field, FieldGrid } from '@/components/admin/detail-parts';
import { StatusBadge } from '@/components/ui/status-badge';
import type { AdminOrganizationDetail } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import { useIdentityKindLabel, useOrgModerationStatusLabel } from '@/lib/labels';
import { organizationStatusTone } from '@/lib/status';

export function VerificationSection({
  organization: org,
}: {
  organization: AdminOrganizationDetail;
}) {
  const t = useTranslations('admin.organizationDetail');
  const common = useTranslations('common');
  const verificationT = useTranslations('admin.verification');
  const statusLabel = useOrgModerationStatusLabel();
  const identityKindLabel = useIdentityKindLabel();
  const verification = org.verification;

  if (!verification) {
    return (
      <DetailSection title={t('section.verification')}>
        <p className="text-[12.5px] text-conc-400">{t('verification.none')}</p>
      </DetailSection>
    );
  }

  const requester = verification.requester;

  return (
    <DetailSection title={t('section.verification')}>
      <FieldGrid>
        <Field label={t('verification.status')}>
          <StatusBadge tone={organizationStatusTone(verification.status)}>
            {statusLabel(verification.status)}
          </StatusBadge>
        </Field>
        <Field label={t('verification.submitted')}>
          {verification.submittedAt && (
            <span className="num">{isoDate(verification.submittedAt)}</span>
          )}
        </Field>
        <Field label={t('verification.decided')}>
          {verification.decidedAt && <span className="num">{isoDate(verification.decidedAt)}</span>}
        </Field>
        <Field label={t('verification.reason')}>
          {verification.decisionReason && <span dir="auto">{verification.decisionReason}</span>}
        </Field>
        {requester && (
          <>
            <Field label={t('verification.requester')}>
              {requester.fullName}
              {requester.identityKind && (
                <span className="ms-1.5 font-normal text-conc-500">
                  · {identityKindLabel(requester.identityKind)}
                </span>
              )}
            </Field>
            {/* Not collected at registration today; the row appears once it is. */}
            {requester.jobTitle && (
              <Field label={t('verification.jobTitle')}>{requester.jobTitle}</Field>
            )}
            <Field label={t('verification.phone')}>
              {requester.phone && (
                <ContactValue value={requester.phone} copyLabel={t('copyPhone')} numeric />
              )}
            </Field>
            <Field label={t('verification.email')}>
              {requester.email && (
                <ContactValue value={requester.email} copyLabel={common('copyEmail')} />
              )}
            </Field>
          </>
        )}
      </FieldGrid>
      {verification.letterPreviewUrl && (
        <a
          href={verification.letterPreviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 text-[13px] font-semibold text-pitch-700 hover:underline"
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
          {verificationT('viewLetter')}
        </a>
      )}
    </DetailSection>
  );
}
